import { FC, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { AuthenticatedHeader } from 'app/features/common/headers/authenticatedHeader'
import { useFormRepository } from 'app/hooks/useFormRepository'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Loading, LottieAnimationName, IconButton } from '@tuhi/components'
import { FormsAuthenticatedHeader } from 'app/features/forms/components/formsAuthenticatedHeader'
import { AccForm, convertFormDtoToAccForm } from 'app/api/forms/dto/accFormDto'
import { FormReview } from 'app/features/forms/review'
import { FormDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'
import { extractReportTitle } from 'app/api/forms/utils/questionnaireResponseUtils'
import ChevronLeftSvg from '@tuhi/components/assets/icons/buttons/chevronLeft.svg'

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr;
`

const LoadingSpinner = styled(Loading)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

const FadeInContent = styled.div<{ $ready: boolean }>`
    opacity: ${(p) => (p.$ready ? 1 : 0)};
    visibility: ${(p) => (p.$ready ? 'visible' : 'hidden')};
    animation: ${(p) => (p.$ready ? fadeIn : 'none')} 0.3s ease-out both;
`

const BackButton = styled(IconButton)`
    margin-right: 8px;
`

export const FormReviewRoute: FC = () => {
    const { formId } = useParams<{ formId: string }>()
    const navigate = useNavigate()
    const location = useLocation()
    const { subscribeToForm } = useFormRepository()
    const [form, setForm] = useState<AccForm | null>(null)
    const [strippedFields, setStrippedFields] = useState<string[]>([])
    const [title, setTitle] = useState('')
    const [isReady, setIsReady] = useState(false)
    const mountTimeRef = useRef(performance.now())
    const callbackCountRef = useRef(0)

    // Log mount
    useEffect(() => {
        logger.info('[perf][form-review-route] Mounted', {
            action: 'perf_form_review_route_mount',
            metadata: { formId, mountTime: Math.round(mountTimeRef.current) },
        })
        return () => {
            logger.info('[perf][form-review-route] Unmounted', {
                action: 'perf_form_review_route_unmount',
                metadata: { formId },
            })
        }
    }, [formId])

    // Get the return path from navigation state, default to forms list
    const returnTo =
        (location.state as { returnTo?: string })?.returnTo || '/forms'
    const autoNavigated =
        (location.state as { autoNavigated?: boolean })?.autoNavigated ?? false

    // When auto-navigated from form creation, hold the loading spinner
    // a bit longer so the user doesn't see a flash of unresolved content.
    const [holdLoading, setHoldLoading] = useState(autoNavigated)

    useEffect(() => {
        if (!holdLoading) return
        // Release after form data arrives + a small buffer for ValueSet resolution to start
        if (form) {
            logger.info(
                '[perf][form-review-route] holdLoading: form arrived, starting 800ms buffer',
                {
                    action: 'perf_hold_loading_buffer_start',
                    metadata: {
                        formId,
                        msSinceMount: Math.round(
                            performance.now() - mountTimeRef.current
                        ),
                    },
                }
            )
            const timeout = setTimeout(() => {
                logger.info(
                    '[perf][form-review-route] holdLoading: buffer expired, releasing content',
                    {
                        action: 'perf_hold_loading_buffer_release',
                        metadata: {
                            formId,
                            msSinceMount: Math.round(
                                performance.now() - mountTimeRef.current
                            ),
                        },
                    }
                )
                setHoldLoading(false)
            }, 800)
            return () => clearTimeout(timeout)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- formId is only logged; the 800ms buffer must not restart on it
    }, [form, holdLoading])

    const formatTitle = (
        patientId: string,
        patientName: string,
        _reportTitle?: string | null
    ) => {
        // Format: NHI | Patient Name
        return `${patientId} | ${patientName}`
    }

    const handleBack = () => {
        logger.debug('Back button clicked in form review', {
            action: 'form_review_back_click',
            metadata: { returnTo, formId },
        })
        navigate(returnTo)
    }

    useEffect(() => {
        if (!formId) {
            logger.error('No form ID provided', new Error('Missing formId'), {
                action: 'form_review_no_id',
            })
            return
        }

        const subscribeStart = performance.now()
        logger.info('[perf][form-review-route] Setting up form subscription', {
            action: 'perf_setup_form_subscription',
            metadata: {
                formId,
                msSinceMount: Math.round(subscribeStart - mountTimeRef.current),
            },
        })

        const subscription = subscribeToForm(formId, (formDto: FormDto) => {
            callbackCountRef.current++
            const callbackTime = performance.now()
            logger.info(
                '[perf][form-review-route] Form subscription callback fired',
                {
                    action: 'perf_form_callback',
                    metadata: {
                        formId: formDto.id,
                        type: formDto.type,
                        status: formDto.status,
                        callbackNumber: callbackCountRef.current,
                        msSinceMount: Math.round(
                            callbackTime - mountTimeRef.current
                        ),
                        msSinceSubscribe: Math.round(
                            callbackTime - subscribeStart
                        ),
                    },
                }
            )

            try {
                const convertStart = performance.now()
                const convertedForm = convertFormDtoToAccForm(formDto)
                const convertMs = performance.now() - convertStart

                logger.info(
                    '[perf][form-review-route] Form converted, calling setForm',
                    {
                        action: 'perf_form_converted',
                        metadata: {
                            formId: formDto.id,
                            convertMs: Math.round(convertMs),
                            sectionCount: convertedForm.sections?.length,
                            callbackNumber: callbackCountRef.current,
                            msSinceMount: Math.round(
                                performance.now() - mountTimeRef.current
                            ),
                        },
                    }
                )

                setForm(convertedForm)

                // Extract strippedFields from FormDto (filter out nulls)
                const stripped = (formDto.strippedFields ?? []).filter(
                    (s): s is string => s !== null
                )
                setStrippedFields(stripped)

                // Format title with patient NHI and name
                setTitle(
                    formatTitle(
                        formDto.patientId,
                        formDto.patientName,
                        extractReportTitle(formDto.response)
                    )
                )
            } catch (error) {
                const err =
                    error instanceof Error
                        ? error
                        : new Error('Form conversion failed')
                logger.error('Failed to convert FormDto to AccForm', err, {
                    action: 'form_conversion_error',
                    metadata: {
                        formId: formDto.id,
                        error: err.message,
                    },
                })
            }
        })

        logger.info('[perf][form-review-route] subscribeToForm returned', {
            action: 'perf_subscribe_returned',
            metadata: {
                formId,
                hasSubscription: !!subscription,
                msSinceMount: Math.round(
                    performance.now() - mountTimeRef.current
                ),
            },
        })

        return () => {
            if (subscription) {
                logger.debug('Unsubscribing from form', {
                    action: 'unsubscribe_form',
                    metadata: { formId },
                })
                subscription.unsubscribe()
            }
        }
    }, [formId, subscribeToForm])

    const loading = (
        <LoadingSpinner
            name={LottieAnimationName.LoadSpinner}
            width="120px"
            height="120px"
            message="Loading..."
        />
    )

    const handleReady = () => {
        logger.info('[perf][form-review-route] FormReview reported ready', {
            action: 'perf_form_review_route_child_ready',
            metadata: {
                formId,
                msSinceMount: Math.round(
                    performance.now() - mountTimeRef.current
                ),
            },
        })
        setIsReady(true)
    }

    const showContent = form && isReady && !holdLoading

    logger.info('[perf][form-review-route] Render', {
        action: 'perf_form_review_route_render',
        metadata: {
            formId,
            hasForm: !!form,
            isReady,
            holdLoading,
            showContent: !!showContent,
            autoNavigated,
            msSinceMount: Math.round(performance.now() - mountTimeRef.current),
        },
    })

    return (
        <Container>
            {!showContent && loading}
            {form && (
                <FadeInContent $ready={!!showContent}>
                    <AuthenticatedHeader title={title} hasGap={false}>
                        <BackButton onClick={handleBack}>
                            <ChevronLeftSvg />
                        </BackButton>
                        <FormsAuthenticatedHeader form={form} />
                    </AuthenticatedHeader>
                    <FormReview
                        form={form}
                        strippedFields={strippedFields}
                        onReady={handleReady}
                    />
                </FadeInContent>
            )}
        </Container>
    )
}
